/**
 * Image Conversion & Optimization Utility
 * Converts user/camera images to optimized WebP / JPEG format with guaranteed
 * size limit (< 250KB) to ensure reliable persistence in Firestore & cloud databases.
 */

export interface WebPConversionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.0 to 1.0 (default 0.8)
  maxSizeBytes?: number; // default 250000 (~245KB for fast Firestore saving)
  preferredFormat?: 'image/webp' | 'image/jpeg';
}

export interface WebPConversionResult {
  file: File;
  dataUrl: string;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
}

/**
 * Calculates byte size of a base64 Data URL string
 */
export function getDataUrlByteSize(dataUrl: string): number {
  if (!dataUrl || !dataUrl.includes(',')) return 0;
  const base64Str = dataUrl.split(',')[1] || '';
  const padding = (base64Str.match(/=/g) || []).length;
  return Math.max(0, Math.round((base64Str.length * 3) / 4 - padding));
}

/**
 * Formats byte size into human readable string (e.g. "124 KB")
 */
export function formatImageSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Converts a File, Blob, or existing Data URL to an optimized, compressed Data URL string.
 * - Scales down large camera photos (e.g. 12MP/48MP) to reasonable dimensions (default max 1200px)
 * - Automatically falls back to high-compression JPEG if WebP is unsupported or larger in Safari/WebKit
 * - Guarantees data URL size stays under maxSizeBytes (default 250KB) for instant Firestore writes
 */
export function convertToWebPDataUrl(
  fileOrBlobOrUrl: File | Blob | string,
  options: WebPConversionOptions = {}
): Promise<string> {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    maxSizeBytes = 250000,
    preferredFormat = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    // If it's already a clean string URL (http/https), return as-is
    if (typeof fileOrBlobOrUrl === 'string' && /^https?:\/\//i.test(fileOrBlobOrUrl)) {
      return resolve(fileOrBlobOrUrl);
    }

    const processImageElement = (img: HTMLImageElement) => {
      try {
        let currentWidth = img.naturalWidth || img.width;
        let currentHeight = img.naturalHeight || img.height;

        if (!currentWidth || !currentHeight) {
          return resolve(img.src);
        }

        // Calculate proportional scale
        let targetWidth = currentWidth;
        let targetHeight = currentHeight;

        if (targetWidth > maxWidth) {
          targetHeight = Math.round((targetHeight * maxWidth) / targetWidth);
          targetWidth = maxWidth;
        }

        if (targetHeight > maxHeight) {
          targetWidth = Math.round((targetWidth * maxHeight) / targetHeight);
          targetHeight = maxHeight;
        }

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) {
          return resolve(img.src);
        }

        // High quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Fill white background for transparent images converted to JPEG
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Try preferred format with fallback
        let formatToUse = preferredFormat;
        let currentQuality = quality;
        let outputDataUrl = canvas.toDataURL(formatToUse, currentQuality);

        // If WebP was requested but browser exported PNG (Safari/WebKit fallback), switch to JPEG
        if (formatToUse === 'image/webp' && !outputDataUrl.startsWith('data:image/webp')) {
          formatToUse = 'image/jpeg';
          outputDataUrl = canvas.toDataURL('image/jpeg', currentQuality);
        }

        // Check size: If larger than maxSizeBytes, iteratively compress or downscale
        let attempts = 0;
        let currentByteSize = getDataUrlByteSize(outputDataUrl);

        while (currentByteSize > maxSizeBytes && attempts < 4) {
          attempts++;
          currentQuality = Math.max(0.4, currentQuality - 0.15);

          // If quality reduction isn't enough, downscale canvas dimensions
          if (attempts >= 2) {
            targetWidth = Math.round(targetWidth * 0.8);
            targetHeight = Math.round(targetHeight * 0.8);
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, targetWidth, targetHeight);
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          }

          outputDataUrl = canvas.toDataURL(formatToUse, currentQuality);
          currentByteSize = getDataUrlByteSize(outputDataUrl);
        }

        resolve(outputDataUrl);
      } catch (err) {
        console.warn('[imageUtils] Fallback on compression error:', err);
        if (img.src) {
          resolve(img.src);
        } else {
          reject(err);
        }
      }
    };

    if (typeof fileOrBlobOrUrl === 'string') {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => processImageElement(img);
      img.onerror = () => resolve(fileOrBlobOrUrl);
      img.src = fileOrBlobOrUrl;
    } else {
      // It's a File or Blob
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Erro ao ler a foto da câmera ou arquivo.'));
      reader.onload = (e) => {
        const dataStr = e.target?.result as string;
        if (!dataStr) {
          return reject(new Error('Não foi possível ler a imagem.'));
        }
        const img = new Image();
        img.onload = () => processImageElement(img);
        img.onerror = () => reject(new Error('Formato de imagem não suportado.'));
        img.src = dataStr;
      };
      reader.readAsDataURL(fileOrBlobOrUrl);
    }
  });
}

/**
 * Converts a File object to an optimized File object.
 */
export function convertToWebPFile(
  file: File,
  options: WebPConversionOptions = {}
): Promise<File> {
  return new Promise((resolve, reject) => {
    convertToWebPDataUrl(file, options)
      .then((dataUrl) => {
        const arr = dataUrl.split(',');
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        const blob = new Blob([u8arr], { type: mime });
        const ext = mime.includes('webp') ? 'webp' : 'jpg';
        const newFileName = file.name.substring(0, file.name.lastIndexOf('.')) || 'photo';
        const finalFileName = `${newFileName}.${ext}`;

        const finalFile = new File([blob], finalFileName, {
          type: mime,
          lastModified: Date.now(),
        });

        resolve(finalFile);
      })
      .catch((err) => reject(err));
  });
}

/**
 * Full conversion function returning metadata, file and Data URL
 */
export async function processImageToWebP(
  file: File,
  options: WebPConversionOptions = {}
): Promise<WebPConversionResult> {
  const dataUrl = await convertToWebPDataUrl(file, options);
  const webpFile = await convertToWebPFile(file, options);
  const byteSize = getDataUrlByteSize(dataUrl);

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        file: webpFile,
        dataUrl,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        originalSize: file.size,
        compressedSize: byteSize,
      });
    };
    img.src = dataUrl;
  });
}

